import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import CoreDataProvider from 'core/data';
import { useAuth } from 'oidc-react';
import useAuthenticated, { authenticate } from 'core/data/authenticated';
import ValidateGroupAccess from 'components/validate-group-access';
import { SnackbarProvider } from 'components/snackbar';
import DisplaySnackbar from 'components/snackbar/component';
import {
  ThemeProvider as MaterialThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'theme/styled';
import materialTheme from 'theme';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { localeSet } from 'translation';
import ScrollToTop from 'core/components/scroll-to-top';
import GlobalStyle from './theme/styled/global-style';
import AuthenticatedRoute from './components/authenticated-route';

import Callback from './pages/oauth/callback';
import Logout from './pages/logout';
import Index from './pages/index';
import Onboarding from './pages/onboarding';
import AppPage from 'pages/app';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

export default function App(): JSX.Element {
  const [authenticatedState, setAuthenticated] = useAuthenticated();
  const { signIn } = useAuth();
  const queryClient = new QueryClient();

  useEffect(() => {
    if (authenticatedState === 'authenticated') return;

    const path = window.location.pathname;
    if (path.indexOf('app') === -1) return;
    authenticate().then(state => {
      if (state === 'unauthenticated') signIn();
      setAuthenticated(state);
    });
  }, [authenticatedState]);

  return (
    <StyledEngineProvider injectFirst>
      <MaterialThemeProvider theme={materialTheme}>
        <StyledThemeProvider theme={materialTheme}>
          <GlobalStyle />
          <CssBaseline />
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={localeSet}
          >
            <Router>
              <ScrollToTop />
              <SnackbarProvider>
                <DisplaySnackbar />
                <Switch>
                  <Route exact path='/signup' component={Onboarding} />
                  <Route exact path='/oauth/callback' component={Callback} />
                  <Route exact path='/logout' component={Logout} />

                  <Route exact path='/' component={Index} />
                  <CoreDataProvider state={authenticatedState}>
                    <QueryClientProvider client={queryClient}>
                      <ValidateGroupAccess>
                        <AuthenticatedRoute
                          auth={authenticatedState}
                          path='/app/:appId'
                          component={AppPage}
                        />
                      </ValidateGroupAccess>
                    </QueryClientProvider>
                  </CoreDataProvider>
                </Switch>
              </SnackbarProvider>
            </Router>
          </LocalizationProvider>
        </StyledThemeProvider>
      </MaterialThemeProvider>
    </StyledEngineProvider>
  );
}
